import React from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const HERO_BG =
  "https://images.unsplash.com/photo-1764204113218-e595e122ff51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxvaWwlMjB0YW5rZXIlMjBzaGlwJTIwcG9ydCUyMGR1c2t8ZW58MHx8fHwxNzgzNTkxNTM3fDA&ixlib=rb-4.1.0&q=85";

const Hero = () => {
  const { t } = useT();
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden grain"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Deepwater port terminal at dusk"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/80 via-[#0A192F]/88 to-[#020C1B]" />
        <div className="absolute inset-0 bg-[radial-gradient(1000px_600px_at_20%_20%,rgba(212,175,55,0.08),transparent_60%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-32 md:py-40 w-full">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-8" data-testid="hero-eyebrow">
            <div className="hairline-crimson" />
            <span className="font-sans text-[11px] tracked uppercase text-[#D4AF37]" style={{ letterSpacing: "0.32em" }}>
              {t.hero.eyebrow}
            </span>
          </div>

          <h1
            className="font-serif text-bone leading-[1.02] mb-8"
            style={{ fontWeight: 500 }}
            data-testid="hero-headline"
          >
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl">{t.hero.h1a}</span>
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#D4AF37] italic" style={{ fontWeight: 400 }}>{t.hero.h1b}</span>
            <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl">{t.hero.h1c}</span>
          </h1>

          <p
            className="font-sans text-base md:text-lg text-steel max-w-2xl mb-12 leading-relaxed"
            data-testid="hero-subheadline"
          >
            {t.hero.sub}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollTo("#products")}
              className="btn-crimson group inline-flex items-center justify-center gap-3 px-7 py-4 font-sans text-[13px] font-semibold uppercase tracked"
              style={{ letterSpacing: "0.22em", minHeight: 52 }}
            >
              {t.hero.cta_primary}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollTo("#compliance")}
              className="btn-gold-outline inline-flex items-center justify-center gap-3 px-7 py-4 font-sans text-[13px] font-semibold uppercase tracked"
              style={{ letterSpacing: "0.22em", minHeight: 52 }}
            >
              <ShieldCheck size={16} />
              {t.hero.cta_secondary}
            </button>
          </div>

          <div className="mt-16 pt-8 border-t border-[#D4AF37]/15 grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="hero-trust-ribbon">
            {t.hero.trust.map((it) => (
              <div key={it.k} className="flex flex-col">
                <span className="font-sans text-[10px] uppercase tracked text-[#D4AF37]" style={{ letterSpacing: "0.28em" }}>{it.k}</span>
                <span className="font-serif text-bone text-lg mt-1">{it.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#7A1C1C]" />
    </section>
  );
};

export default Hero;
